import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Select, 
  Row, 
  Col, 
  Statistic, 
  message, 
  Modal, 
  Form, 
  Input, 
  Typography,
  Space,
  Tooltip,
  Badge
} from 'antd';
import { 
  InboxOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined, 
  CheckCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  MessageOutlined,
  ExperimentOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const WorkBasket = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workItems, setWorkItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: ['Pending', 'In Progress', 'Overdue'],
    type: null,
    priority: null
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Fetch work basket data
  const fetchWorkBasket = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add filters to params
      if (filters.status?.length) {
        filters.status.forEach(status => params.append('status', status));
      }
      if (filters.type) {
        params.append('type', filters.type);
      }
      if (filters.priority) {
        params.append('priority', filters.priority);
      }
      
      const response = await axios.get(`/work-items/my-basket?${params.toString()}`);
      setWorkItems(response.data.data.workItems || []);
    } catch (error) {
      console.error('Error fetching work basket:', error);
      message.error('Failed to load work basket');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get('/work-items/stats');
      setStats(response.data.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchWorkBasket();
    fetchStats();
  }, [filters]);

  // Handle status update
  const handleStatusUpdate = async (workItemId, newStatus, note = '') => {
    try {
      const payload = { status: newStatus };
      if (note) {
        payload.note = note;
      }
      
      await axios.patch(`/work-items/${workItemId}`, payload);
      message.success('Work item updated successfully');
      fetchWorkBasket();
      fetchStats();
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error updating work item:', error);
      message.error(error.response?.data?.message || 'Failed to update work item');
    }
  };

  // Get icon for work item type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'Order':
        return <FileTextOutlined />;
      case 'EncounterNote':
        return <FileTextOutlined />;
      case 'Message':
        return <MessageOutlined />;
      case 'LabResult':
        return <ExperimentOutlined />;
      default:
        return <InboxOutlined />;
    }
  };

  // Get color for priority
  const getPriorityColor = (priority) => {
    return priority === 'Urgent' ? 'red' : 'blue';
  };

  // Get color for status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'orange';
      case 'In Progress':
        return 'blue';
      case 'Overdue':
        return 'red';
      case 'Completed':
        return 'green';
      default:
        return 'default';
    }
  };

  // Handle quick action
  const handleQuickAction = (record, action) => {
    setSelectedItem(record);
    if (action === 'complete') {
      handleStatusUpdate(record._id, 'Completed');
    } else if (action === 'start') {
      handleStatusUpdate(record._id, 'In Progress');
    } else if (action === 'details') {
      setModalVisible(true);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type) => (
        <Tooltip title={type}>
          {getTypeIcon(type)}
        </Tooltip>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority}
        </Tag>
      )
    },
    {
      title: 'Patient',
      dataIndex: 'patient',
      key: 'patient',
      width: 150,
      render: (patient) => (
        <div>
          <Text strong>{patient?.firstName} {patient?.lastName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            MRN: {patient?.mrn}
          </Text>
        </div>
      )
    },
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <div>
          <Text strong>{title}</Text>
          {record.description && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description.length > 50 
                  ? `${record.description.substring(0, 50)}...` 
                  : record.description
                }
              </Text>
            </>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => (
        <div>
          <Text>{moment(date).format('MM/DD/YY')}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {moment(date).format('HH:mm')}
          </Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          {record.status === 'Pending' && (
            <Button 
              size="small" 
              type="primary"
              onClick={() => handleQuickAction(record, 'start')}
            >
              Start
            </Button>
          )}
          {record.status === 'In Progress' && (
            <Button 
              size="small" 
              type="primary"
              onClick={() => handleQuickAction(record, 'complete')}
            >
              Complete
            </Button>
          )}
          <Button 
            size="small"
            onClick={() => handleQuickAction(record, 'details')}
          >
            Details
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <InboxOutlined /> My Work Basket
        </Title>
        <Text type="secondary">
          Manage your assigned tasks and workflow items
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Items"
              value={stats.totalItems || 0}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Pending"
              value={stats.pendingItems || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={stats.overdueItems || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Urgent"
              value={stats.urgentItems || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Text strong>Status: </Text>
            <Select
              mode="multiple"
              placeholder="Filter by status"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%', marginTop: '8px' }}
            >
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Overdue">Overdue</Option>
              <Option value="Completed">Completed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Text strong>Type: </Text>
            <Select
              placeholder="Filter by type"
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value })}
              style={{ width: '100%', marginTop: '8px' }}
              allowClear
            >
              <Option value="Order">Orders</Option>
              <Option value="EncounterNote">Encounter Notes</Option>
              <Option value="Message">Messages</Option>
              <Option value="LabResult">Lab Results</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Text strong>Priority: </Text>
            <Select
              placeholder="Filter by priority"
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value })}
              style={{ width: '100%', marginTop: '8px' }}
              allowClear
            >
              <Option value="Routine">Routine</Option>
              <Option value="Urgent">Urgent</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                fetchWorkBasket();
                fetchStats();
              }}
              style={{ marginTop: '24px' }}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Work Items Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={workItems}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          scroll={{ x: true }}
        />
      </Card>

      {/* Work Item Details Modal */}
      <Modal
        title="Work Item Details"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedItem && (
          <div>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={12}>
                <Text strong>Type: </Text>
                <Tag>{selectedItem.type}</Tag>
              </Col>
              <Col span={12}>
                <Text strong>Priority: </Text>
                <Tag color={getPriorityColor(selectedItem.priority)}>
                  {selectedItem.priority}
                </Tag>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Text strong>Patient: </Text>
                <Text>
                  {selectedItem.patient?.firstName} {selectedItem.patient?.lastName} 
                  (MRN: {selectedItem.patient?.mrn})
                </Text>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Text strong>Task: </Text>
                <Text>{selectedItem.title}</Text>
              </Col>
            </Row>
            
            {selectedItem.description && (
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col span={24}>
                  <Text strong>Description: </Text>
                  <Text>{selectedItem.description}</Text>
                </Col>
              </Row>
            )}
            
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => {
                handleStatusUpdate(selectedItem._id, values.status, values.note);
              }}
            >
              <Form.Item
                name="status"
                label="Update Status"
                initialValue={selectedItem.status}
              >
                <Select>
                  <Option value="Pending">Pending</Option>
                  <Option value="In Progress">In Progress</Option>
                  <Option value="Completed">Completed</Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                name="note"
                label="Add Note (Optional)"
              >
                <TextArea rows={3} placeholder="Add a note about this work item..." />
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Update
                  </Button>
                  <Button onClick={() => {
                    setModalVisible(false);
                    form.resetFields();
                  }}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkBasket;
